import React, { useState, useEffect } from 'react';
import { Mail, Plus, Trash2, ToggleLeft, ToggleRight, Edit3, Check, X } from 'lucide-react';
import { API_URL } from '../services/api';

interface AdminEmail {
  id: number;
  email: string;
  name?: string;
  notification_types: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailManagementProps {
  onClose: () => void;
}

const EmailManagement: React.FC<EmailManagementProps> = ({ onClose }) => {
  const [emails, setEmails] = useState<AdminEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  
  // Form states
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [examNotifications, setExamNotifications] = useState(true);
  const [courseNotifications, setCourseNotifications] = useState(true);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/emails`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch admin emails');
      }
      
      const data = await response.json();
      setEmails(data);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formEmail) {
      setError('Email is required');
      return;
    }

    const notificationTypes = [];
    if (examNotifications) notificationTypes.push('exam_completion');
    if (courseNotifications) notificationTypes.push('course_completion');

    try {
      const response = await fetch(`${API_URL}/admin/emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: formEmail,
          name: formName || null,
          notification_types: notificationTypes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add email');
      }

      resetForm();
      setShowAddForm(false);
      fetchEmails();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleUpdateEmail = async (id: number) => {
    const email = emails.find(e => e.id === id);
    if (!email) return;

    const notificationTypes = [];
    if (examNotifications) notificationTypes.push('exam_completion');
    if (courseNotifications) notificationTypes.push('course_completion');

    try {
      const response = await fetch(`${API_URL}/admin/emails/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: formEmail,
          name: formName || null,
          notification_types: notificationTypes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update email');
      }

      resetForm();
      setEditingId(null);
      fetchEmails();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDeleteEmail = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this admin email?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/emails/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete email');
      }

      fetchEmails();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/admin/emails/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle email status');
      }

      fetchEmails();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const startEditing = (email: AdminEmail) => {
    setEditingId(email.id);
    setFormEmail(email.email);
    setFormName(email.name || '');
    setExamNotifications(email.notification_types.includes('exam_completion'));
    setCourseNotifications(email.notification_types.includes('course_completion'));
  };

  const cancelEditing = () => {
    setEditingId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormEmail('');
    setFormName('');
    setExamNotifications(true);
    setCourseNotifications(true);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Mail className="mr-2" />
            Email Notification Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-600/20 border border-red-500 rounded-md text-red-400">
            {error}
          </div>
        )}

        {/* Add Email Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-500 transition flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Admin Email
          </button>
        </div>

        {/* Add Email Form */}
        {showAddForm && (
          <div className="mb-6 bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Add New Admin Email</h3>
            <form onSubmit={handleAddEmail}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notification Types
                </label>
                <div className="space-y-2">
                  <label className="flex items-center text-gray-300">
                    <input
                      type="checkbox"
                      checked={examNotifications}
                      onChange={(e) => setExamNotifications(e.target.checked)}
                      className="mr-2"
                    />
                    Exam Completion Notifications
                  </label>
                  <label className="flex items-center text-gray-300">
                    <input
                      type="checkbox"
                      checked={courseNotifications}
                      onChange={(e) => setCourseNotifications(e.target.checked)}
                      className="mr-2"
                    />
                    Course Completion Notifications
                  </label>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500"
                >
                  Add Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Email List */}
        {loading ? (
          <div className="text-center text-gray-400">Loading...</div>
        ) : (
          <div className="space-y-4">
            {emails.map((email) => (
              <div key={email.id} className="bg-gray-700 p-4 rounded-lg">
                {editingId === email.id ? (
                  // Edit Mode
                  <form onSubmit={(e) => { e.preventDefault(); handleUpdateEmail(email.id); }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formEmail}
                          onChange={(e) => setFormEmail(e.target.value)}
                          className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Notification Types
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center text-gray-300 text-sm">
                          <input
                            type="checkbox"
                            checked={examNotifications}
                            onChange={(e) => setExamNotifications(e.target.checked)}
                            className="mr-2"
                          />
                          Exam Completion Notifications
                        </label>
                        <label className="flex items-center text-gray-300 text-sm">
                          <input
                            type="checkbox"
                            checked={courseNotifications}
                            onChange={(e) => setCourseNotifications(e.target.checked)}
                            className="mr-2"
                          />
                          Course Completion Notifications
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-500 text-sm flex items-center"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="bg-gray-600 text-white px-3 py-1 rounded-md hover:bg-gray-500 text-sm flex items-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  // View Mode
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Mail className="h-4 w-4 mr-2 text-blue-400" />
                        <span className="text-white font-medium">{email.email}</span>
                        {email.name && (
                          <span className="text-gray-400 ml-2">({email.name})</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        Notifications: {email.notification_types.join(', ')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Added: {new Date(email.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(email.id)}
                        className={`flex items-center ${
                          email.is_active 
                            ? 'text-green-400 hover:text-green-300' 
                            : 'text-gray-400 hover:text-gray-300'
                        }`}
                        title={email.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {email.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => startEditing(email)}
                        className="text-blue-400 hover:text-blue-300"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteEmail(email.id)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {emails.length === 0 && (
              <div className="text-center text-gray-400 py-8">
                No admin emails configured. Add one to start receiving notifications.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailManagement; 